// src/components/shared/DemoAcknowledgmentModal.js

import React, { useRef, useEffect } from "react";
import styled from "styled-components";
import Modal from "./Modal";
import { spotlightWhenStable } from "../../utils/spotlightUtils";
import {
  DocumentTextIcon,
  PencilSquareIcon,
  BookmarkIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  DocumentChartBarIcon,
  DocumentIcon,
  ArrowsPointingOutIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";

// =========== styled wrappers ===========

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.2rem;
  margin-bottom: 0.25rem;
`;

const TitleCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.18em;   /* Controls vertical gap between title & subtitle */
  min-width: 0;
`;

const TopRightButton = styled.button`
  background: #1e40af;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.90rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  margin-top: 0.05em;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;        /* 8px */
  margin: 0.5rem 0;   /* 8px top & bottom */
`;

const Card = styled.div`
  background: ${({ theme }) => theme.isDarkMode ? "#111827" : theme.colors.neutral[50]};
  border-radius: 8px;        /* 8px */
  padding: 0.5rem;           /* 8px */
  box-shadow: ${({ theme }) =>
    theme.isDarkMode
      ? "0 1px 2px rgba(0, 0, 0, 0.5)"
      : "0 1px 2px rgba(0, 0, 0, 0.05)"};
  font-size: 0.75rem;        /* 12px */
  line-height: 1.4;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;               /* 8px */
  transition: all 0.2s ease;
  position: relative;
  border: ${({ theme }) =>
    theme.isDarkMode
      ? "1px solid rgba(255, 255, 255, 0.1)"
      : "1px solid rgba(0, 0, 0, 0.06)"};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${({ theme }) =>
    theme.isDarkMode
      ? "0 2px 4px rgba(0, 0, 0, 0.6)"
      : "0 2px 4px rgba(0, 0, 0, 0.1)"};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;               /* 8px */

  svg {
    width: 1rem;             /* 16px */
    height: 1rem;
    color: ${({ theme }) => theme.isDarkMode ? "#60a5fa" : theme.colors.primary[600]};
  }

  h3 {
    font-size: 0.875rem;     /* 14px */
    font-weight: 600;
    margin: 0;
    color: ${({ theme }) => theme.isDarkMode ? "#ffffff" : theme.colors.neutral[800]};
  }
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${({ theme }) =>
    theme.isDarkMode ? "rgba(96,165,250,0.2)" : "rgba(37,99,235,0.1)"};
  color: ${({ theme }) => theme.isDarkMode ? "#93c5fd" : theme.colors.primary[700]};
  padding: 0.25rem 0.5rem;    /* 4px 8px */
  border-radius: 9999px;
  font-size: 0.625rem;        /* 10px */
  font-weight: 500;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.75rem;          /* 12px */
  color: ${({ theme }) => theme.isDarkMode ? "#e5e7eb" : theme.colors.neutral[700]};

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;               /* 4px */
    margin: 0.25rem 0;          /* 4px top/bottom */

    &::before {
      content: "•";
      margin-right: 4px;
      color: ${({ theme }) => theme.isDarkMode ? "#60a5fa" : theme.colors.primary[500]};
      font-weight: bold;
    }
  }
`;

const Title = styled.h1`
  font-size: 1rem;     /* 16px */
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
  color: ${({ theme }) => theme.isDarkMode ? "#ffffff" : theme.colors.neutral[900]};
`;

const Subtitle = styled.h2`
  font-size: 0.75rem;   /* 12px */
  font-weight: 500;
  margin: 0;
  line-height: 1.5;
  color: ${({ theme }) => theme.isDarkMode ? "#e5e7eb" : theme.colors.neutral[600]};
`;

const Quote = styled.blockquote`
  grid-column: 1 / -1;
  margin: 0.35rem 0.35rem 0;  /* Halved from 1.2rem 0 0.7rem 0 */
  font-size: 1rem;
  line-height: 1.55;
  font-style: italic;
  text-align: center;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  background: #1e40af;
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 12px 0 #0003;
`;

// =========== feature data ===========

const features = [
  {
    title: "Live Call Transcription",
    icon: DocumentTextIcon,
    tag: "✅ AI-Powered",
    items: [
      "Real-time speaker-labeled transcription",
      "Context-aware CRM field highlights",
      "Full audit trail & secure storage",
    ],
  },
  {
    title: "Data Entry Suggestions",
    icon: PencilSquareIcon,
    tag: "✏️ Editable",
    items: [
      "Auto-detects key client info",
      "One-click CRM updates",
      "Built-in error handling",
    ],
  },
  {
    title: "Smart Highlights",
    icon: BookmarkIcon,
    tag: "✨ Intelligent",
    items: [
      "Tag important moments instantly",
      "AI-generated summaries",
      "Quick post-call review",
    ],
  },
  {
    title: "Guided AI Prompts",
    icon: LightBulbIcon,
    tag: "🧠 Context-Aware",
    items: [
      "Smart question suggestions",
      "CRM field completion nudges",
      "Client-invisible assistance",
    ],
  },
  {
    title: "Task Capture",
    icon: ClipboardDocumentListIcon,
    tag: "📅 Automated",
    items: [
      "Auto-detects follow-up items",
      "Confirm before saving",
      "Links to meeting context",
    ],
  },
  {
    title: "Summary Generator",
    icon: DocumentChartBarIcon,
    tag: "📑 Comprehensive",
    items: [
      "AI-drafted meeting summaries",
      "Includes updates & tasks",
      "Export to PDF/CRM",
    ],
  },
  {
    title: "Notes Section",
    icon: DocumentIcon,
    tag: "📝 Conscious Touch",
    items: [
      "Freeform note space",
      "Auto-saving enabled",
      "Always accessible",
    ],
  },
  {
    title: "Smart Navigation",
    icon: ArrowsPointingOutIcon,
    tag: "🔧 Contextual",
    items: [
      "Context-based tab suggestions",
      "Quick-jump to relevant tools",
      "Streamlined workflow",
    ],
  },
  {
    title: "Compliance Ready",
    icon: ShieldCheckIcon,
    tag: "🔒 Secure",
    items: [
      "FINRA/SEC-conscious design",
      "Complete audit logging",
      "Advisor control & review",
    ],
  },
];

// =========== component ===========

export default function DemoAcknowledgmentModal({
  isOpen,
  onClose,
  buttonText = "Let's Begin →",
}) {
  const buttonRef = useRef(null);

  // Update spotlight trigger to use new utility
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    spotlightWhenStable(buttonRef, {
      disableAutoAdvance: true,
      initialDelay: 300, // Extra delay for "Let's Begin" to allow sidebar expansion
    });
  }, [isOpen, buttonText]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="large"     // Use standardized large size for feature lists
    >
      <HeaderRow>
        <TitleCol>
          <Title>Meet Quanto, your personalized AI sidebar copilot</Title>
          <Subtitle>Your Always-On Meeting Partner for Financial Advisors</Subtitle>
        </TitleCol>
        <TopRightButton ref={buttonRef} onClick={onClose}>
          {buttonText}
        </TopRightButton>
      </HeaderRow>

      <GridContainer>
        {features.map((f, i) => (
          <Card key={i}>
            <CardHeader>
              <f.icon />
              <h3>{f.title}</h3>
            </CardHeader>
            <Tag>{f.tag}</Tag>
            <List>
              {f.items.map((item, j) => (
                <li key={j}>{item}</li>
              ))}
            </List>
          </Card>
        ))}

        <Quote>
          "I stopped typing and started listening. My meetings became conversations, not checklists."
        </Quote>
      </GridContainer>
    </Modal>
  );
}
